import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, HelpCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fireConfetti } from "@/lib/confetti";
import { showXPToast } from "@/lib/xpToast";

/** Student-facing quiz list + taker for a single video's quiz (Creator Studio courses). */
export function QuizPanel({ courseId, videoId }) {
  const [quizzes, setQuizzes] = useState(null);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/courses/${courseId}/quizzes`).then((r) => setQuizzes(r.data)).catch(() => setQuizzes([]));
  }, [courseId]);

  const videoQuizzes = (quizzes || []).filter((q) => q.video_id === videoId);

  const openQuiz = (q) => { setActive(q); setAnswers({}); setResult(null); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/quizzes/${active.id}/submit`, {
        answers: active.questions.map((q) => answers[q.id] ?? -1),
      });
      setResult(data);
      if (data.passed) { fireConfetti(); showXPToast(0, 0, "Quiz Passed!"); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  if (quizzes === null) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (videoQuizzes.length === 0) return <p className="text-sm text-slate-500 text-center py-8" data-testid="quiz-panel-empty">No quiz for this lecture yet.</p>;

  if (!active) {
    return (
      <div className="space-y-2" data-testid="quiz-panel-list">
        {videoQuizzes.map((q) => (
          <button key={q.id} onClick={() => openQuiz(q)} data-testid={`quiz-open-btn-${q.id}`} className="w-full flex items-center justify-between text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3 transition-colors">
            <span className="text-sm font-medium text-white flex items-center gap-2"><HelpCircle className="w-4 h-4 text-indigo-400" /> {q.title}</span>
            {q.best_attempt && <span className={`text-xs font-bold ${q.best_attempt.passed ? "text-emerald-400" : "text-amber-400"}`}>Best: {q.best_attempt.score}%</span>}
          </button>
        ))}
      </div>
    );
  }

  if (result) {
    return (
      <div className="text-center py-4" data-testid="quiz-result">
        {result.passed ? <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" /> : <XCircle className="w-10 h-10 text-amber-400 mx-auto mb-2" />}
        <p className="text-2xl font-heading font-bold text-white">{result.score}%</p>
        <p className="text-xs text-slate-400 mb-4">{result.correct_count}/{result.total} correct · {result.passed ? "Passed!" : "Keep practicing (70% to pass)"}</p>
        <div className="space-y-2 text-left max-h-64 overflow-y-auto">
          {result.review.map((r, i) => (
            <div key={i} className="text-xs bg-white/[0.03] rounded-lg p-2.5">
              <p className="text-slate-300 font-medium">{i + 1}. {r.question}</p>
              <p className={r.your_answer === r.correct_index ? "text-emerald-400 mt-1" : "text-rose-400 mt-1"}>{r.explanation}</p>
            </div>
          ))}
        </div>
        <button onClick={() => setActive(null)} data-testid="quiz-back-btn" className="mt-4 text-xs font-semibold text-indigo-300 hover:text-indigo-200">Back to quizzes</button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="quiz-taker">
      <p className="text-sm font-semibold text-white">{active.title}</p>
      {active.questions.map((q, i) => (
        <div key={q.id}>
          <p className="text-sm text-slate-300 mb-2">{i + 1}. {q.question}</p>
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => (
              <button
                key={oi} onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))} data-testid={`quiz-option-${q.id}-${oi}`}
                className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-colors ${answers[q.id] === oi ? "border-indigo-400 bg-indigo-400/10 text-white" : "border-white/10 text-slate-400 hover:border-white/20"}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button onClick={submit} disabled={submitting || Object.keys(answers).length < active.questions.length} data-testid="quiz-submit-btn" className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Quiz"}
      </button>
    </div>
  );
}
